import { useState } from "react";
import { type BundleFormat, MIN_BUNDLE_FORMATS } from "./bundle.utils";

interface UseBundleSelectionArgs {
  formats: BundleFormat[];
  currentIsbn: string;
  initialSelection: string[];
  isEditing: boolean;
  formatsReady: boolean;
}

export const useBundleSelection = ({
  formats,
  currentIsbn,
  initialSelection,
  isEditing,
  formatsReady,
}: UseBundleSelectionArgs) => {
  const [selected, setSelected] = useState<Set<string>>(() => {
    const initial = new Set(initialSelection);
    initial.add(currentIsbn);
    return initial;
  });

  const toggle = (isbn: string) => {
    if (isbn === currentIsbn) {
      return;
    }
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(isbn)) {
        next.delete(isbn);
      } else {
        next.add(isbn);
      }
      return next;
    });
  };

  const selectAll = () =>
    setSelected(
      new Set([currentIsbn, ...formats.map((format) => format.identifier)]),
    );
  const clearAll = () => setSelected(new Set([currentIsbn]));

  const selectedIsbns = formats
    .filter((format) => selected.has(format.identifier))
    .map((format) => format.identifier);
  const selectedCount = selectedIsbns.length;
  const total = formats.length;
  const canConfirm = formatsReady && selectedCount >= MIN_BUNDLE_FORMATS;
  const allSelected = total > 0 && selectedCount === total;

  // Below MIN_BUNDLE_FORMATS while editing means turn off, not save.
  const isTurnOff =
    isEditing && formatsReady && selectedCount < MIN_BUNDLE_FORMATS;

  return {
    selected,
    toggle,
    selectAll,
    clearAll,
    selectedIsbns,
    selectedCount,
    total,
    canConfirm,
    allSelected,
    isTurnOff,
  };
};
